import { useState, useContext } from "react";
import MainSectionSection from "../../components/MainSectionSection";
import SectionTitle from "../../components/SectionTitle";
import { Download, Plus } from "lucide-react";
import { trpc } from "../../trpc/index";
import MerchantContext from "../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { addMonths, format, isValid, parseISO } from "date-fns";

type UIReportType =
  | "citas"
  | "prospectos"
  | "prospectos_no_alcanzables"
  | "actividad"
  | "propiedades"
  | "conversaciones"
  | "notificaciones"
  | "errores_crm"
  | "sincronizacion_crm";
type ApiReportType =
  | "appointments"
  | "leads"
  | "non_reachable_leads"
  | "integral"
  | "properties"
  | "conversations"
  | "notifications"
  | "crm_errors"
  | "crm_sync_log";
type FileFormat = "excel" | "csv";

const CRM_ERRORS_MERCHANT_ID = "albertoalvarez";

const LAST_JOBS_COUNT = 20;

const REPORT_TYPE_LABELS: Record<ApiReportType, string> = {
  appointments: "Reporte de Citas",
  leads: "Reporte de Prospectos",
  non_reachable_leads: "Reporte de Prospectos No Alcanzables",
  integral: "Reporte Integral",
  properties: "Reporte de Propiedades",
  conversations: "Reporte de Conversaciones",
  notifications: "Reporte de Notificaciones",
  crm_errors: "Reporte de Errores de CRM",
  crm_sync_log: "Reporte de Sincronización CRM",
};

const STATUS_LABELS: Record<string, string> = {
  in_progress: "En progreso",
  done: "Listo",
  failed: "Falló",
};

const FORMAT_LABELS: Record<string, string> = {
  excel: "XLSX",
  csv: "CSV",
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  done: "bg-green-100 text-green-800 border-green-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

// A report may only cover a two-month window; the date inputs bound each other so
// the wider range can neither be picked from the calendar nor typed in.
const MAX_RANGE_MONTHS = 2;
const MIN_REPORT_DATE = "2020-01-01";

// Earliest start date allowed for a given end date (never before MIN_REPORT_DATE).
const earliestStartFor = (endDate: string): string => {
  const floor = shiftMonths(endDate, -MAX_RANGE_MONTHS);
  return floor && floor > MIN_REPORT_DATE ? floor : MIN_REPORT_DATE;
};

// Latest end date allowed for a given start date (never in the future).
const latestEndFor = (startDate: string, today: string): string => {
  const ceiling = shiftMonths(startDate, MAX_RANGE_MONTHS);
  return ceiling && ceiling < today ? ceiling : today;
};

// Shifts a "yyyy-MM-dd" input value by whole months, or null when it is empty/invalid.
const shiftMonths = (dateInput: string, months: number): string | null => {
  const parsed = parseISO(dateInput);
  return isValid(parsed) ? format(addMonths(parsed, months), "yyyy-MM-dd") : null;
};

const ReportsContainer = () => {
  const [modalErrorMessage, setModalErrorMessage] = useState<string | null>(null);
  const [tableErrorMessage, setTableErrorMessage] = useState<string | null>(null);
  const { merchantId } = useContext(MerchantContext);
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const currentDate = new Date();
  const todayDate = currentDate.toISOString().split("T")[0];
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const [modalSelectedReport, setModalSelectedReport] = useState<UIReportType | null>(null);
  const [modalStartDate, setModalStartDate] = useState(firstDayOfMonth.toISOString().split("T")[0]);
  const [modalEndDate, setModalEndDate] = useState(todayDate);
  const [modalFormat, setModalFormat] = useState("Excel (.xlsx)");

  const trpcUtils = trpc.useUtils();
  const { mutate: createReportJob, isLoading: isCreatingJob } =
    trpc.merchants.createReportJob.useMutation();

  const getFileFormat = (formatValue: string): FileFormat =>
    formatValue.includes("Excel") ? "excel" : "csv";

  const mapReportTypeToApi = (uiType: UIReportType): ApiReportType => {
    switch (uiType) {
      case "citas":
        return "appointments";
      case "prospectos":
        return "leads";
      case "prospectos_no_alcanzables":
        return "non_reachable_leads";
      case "actividad":
        return "integral";
      case "propiedades":
        return "properties";
      case "conversaciones":
        return "conversations";
      case "notificaciones":
        return "notifications";
      case "errores_crm":
        return "crm_errors";
      case "sincronizacion_crm":
        return "crm_sync_log";
      default:
        throw new Error(`Unknown report type: ${uiType}`);
    }
  };

  const handleModalStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = e.target.value;
    setModalStartDate(newStart);
    if (newStart > modalEndDate) {
      setModalEndDate(newStart);
      return;
    }
    const latestEnd = latestEndFor(newStart, todayDate);
    if (modalEndDate > latestEnd) setModalEndDate(latestEnd);
  };

  const handleModalEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = e.target.value;
    setModalEndDate(newEnd);
    if (newEnd < modalStartDate) {
      setModalStartDate(newEnd);
      return;
    }
    const earliestStart = earliestStartFor(newEnd);
    if (modalStartDate < earliestStart) setModalStartDate(earliestStart);
  };

  const handleGenerateReport = () => {
    if (!modalSelectedReport || !merchantId) return;
    setModalErrorMessage(null);

    const isDateless = modalSelectedReport === "propiedades";

    createReportJob(
      {
        reportType: mapReportTypeToApi(modalSelectedReport),
        createdAt: {
          start: isDateless ? "2000-01-01" : modalStartDate,
          end: isDateless ? "2050-12-31" : modalEndDate,
        },
        fileFormat: getFileFormat(modalFormat),
      },
      {
        onSuccess: async () => {
          await trpcUtils.merchants.listRecentReportJobs.invalidate();
          setIsOpen(false);
        },
        onError: (error: unknown) => {
          const msg =
            error instanceof Error ? error.message : "No se pudo crear el job del reporte";
          setModalErrorMessage(msg);
        },
      }
    );
  };

  const handleDownloadJob = async (jobId: string) => {
    setTableErrorMessage(null);
    try {
      const data = await trpcUtils.merchants.getReportJobDownloadUrl.fetch({ jobId });
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "No se pudo generar el link de descarga";
      setTableErrorMessage(msg);
    }
  };

  const isCrmErrorsMerchant = merchantId === CRM_ERRORS_MERCHANT_ID;

  // One query for every report type and format. Do NOT fan this back out into a `useQuery`
  // per (type, format): the hook count would then depend on merchantId, which resolves after
  // the first render and crashes React's hook order on a direct load of /reports.
  const { data: recentJobs } = trpc.merchants.listRecentReportJobs.useQuery(
    { limit: LAST_JOBS_COUNT },
    {
      enabled: !!merchantId,
      refetchInterval: (data) =>
        data?.some((j: { status: string }) => j.status === "in_progress") ? 8000 : false,
    }
  );

  const allJobs = recentJobs ?? [];

  const openModal = () => {
    setModalSelectedReport(null);
    setModalStartDate(firstDayOfMonth.toISOString().split("T")[0]);
    setModalEndDate(todayDate);
    setModalFormat("Excel (.xlsx)");
    setModalErrorMessage(null);
    setIsOpen(true);
  };

  return (
    <MainSectionSection gap="24px">
      <SectionTitle title="Reportes" />

      <div className="mb-4 flex flex-col flex-wrap items-stretch gap-2 md:flex-row md:items-center md:justify-between">
        <p className="text-base font-medium md:text-lg">Últimos reportes generados</p>
        <Button onClick={openModal}>
          <Plus className="size-4" />
          Generar reporte
        </Button>
      </div>

      {allJobs.length === 0 ? (
        <div className="rounded-md border border-gray-200 py-8 text-center">
          <p className="text-gray-500">
            No hay reportes generados. Use &quot;Generar reporte&quot; para crear uno.
          </p>
        </div>
      ) : isMobile ? (
        <div className="rounded-md border border-gray-200">
          {allJobs.map((job, index) => (
            <div
              key={job.id}
              className={cn(
                "w-full p-3",
                index < allJobs.length - 1 && "border-b border-gray-200"
              )}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="truncate text-sm font-medium">
                  {REPORT_TYPE_LABELS[job.reportType]}
                </span>
                <Badge
                  className={cn(
                    "text-xs",
                    STATUS_BADGE_CLASSES[job.status] ?? "bg-gray-100 text-gray-800"
                  )}
                >
                  {STATUS_LABELS[job.status] ?? job.status}
                </Badge>
              </div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  {job.fromDate} — {job.toDate}
                </span>
                <span className="text-xs text-gray-500">
                  {FORMAT_LABELS[job.fileFormat] ?? job.fileFormat}
                </span>
              </div>
              {job.status === "done" && (
                <button
                  className="cursor-pointer text-sm font-medium text-purple-600 hover:underline"
                  onClick={() => handleDownloadJob(job.id)}
                >
                  Descargar
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Reporte
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Desde
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Hasta
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Formato
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Link
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {allJobs.map((job) => (
                <tr key={job.id}>
                  <td className="whitespace-nowrap px-4 py-3">
                    {REPORT_TYPE_LABELS[job.reportType]}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{job.fromDate}</td>
                  <td className="whitespace-nowrap px-4 py-3">{job.toDate}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {FORMAT_LABELS[job.fileFormat] ?? job.fileFormat}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {STATUS_LABELS[job.status] ?? job.status}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {job.status === "done" ? (
                      <button
                        className="cursor-pointer font-medium text-purple-600 hover:underline"
                        onClick={() => handleDownloadJob(job.id)}
                      >
                        Descargar
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tableErrorMessage && (
        <Alert status="error">
          <p>{tableErrorMessage}</p>
        </Alert>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-xl">
          <DialogHeader>
            <DialogTitle>Generar nuevo reporte</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1">
            {modalErrorMessage && (
              <Alert status="error" className="mb-4">
                <p>{modalErrorMessage}</p>
              </Alert>
            )}
            <p className="mb-4 text-base font-medium">
              Seleccione el reporte que desea generar:
            </p>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              {(
                [
                  ["citas", "Reporte de Citas", "Listado detallado de citas agendadas"],
                  ["prospectos", "Reporte de Prospectos", "Prospectos e información de contacto"],
                  [
                    "prospectos_no_alcanzables",
                    "Reporte de Prospectos No Alcanzables",
                    "Prospectos a los que no se les pudo entregar ningún mensaje",
                  ],
                  ["actividad", "Reporte Integral", "Prospectos, citas e información adicional"],
                  [
                    "propiedades",
                    "Reporte de Propiedades",
                    "Listado de propiedades en inventario",
                  ],
                  [
                    "conversaciones",
                    "Reporte de Conversaciones",
                    "Prospectos con sus conversaciones",
                  ],
                  [
                    "notificaciones",
                    "Reporte de Notificaciones",
                    "Notificaciones de WhatsApp enviadas y su estado",
                  ],
                  ...(isCrmErrorsMerchant
                    ? ([
                        [
                          "errores_crm",
                          "Reporte de Errores de CRM",
                          "Prospectos que no se pudieron crear en el CRM.",
                        ],
                        [
                          "sincronizacion_crm",
                          "Reporte de Sincronización CRM",
                          "Cada llamada al CRM con su payload, respuesta y estado.",
                        ],
                      ] as const)
                    : []),
                ] as const
              ).map(([type, title, desc]) => (
                <div
                  key={type}
                  className={cn(
                    "cursor-pointer rounded-md border p-4",
                    modalSelectedReport === type
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 bg-white"
                  )}
                  onClick={() => setModalSelectedReport(type)}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <Checkbox
                      checked={modalSelectedReport === type}
                      onCheckedChange={() => setModalSelectedReport(type)}
                      className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    />
                    <span className="font-medium">{title}</span>
                  </div>
                  <p className="text-sm text-gray-600">{desc}</p>
                </div>
              ))}
            </div>

            {modalSelectedReport && (
              <div>
                <p className="mb-3 font-medium">Filtros</p>
                {modalSelectedReport !== "propiedades" && (
                  <div className="mb-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="mb-1 text-xs text-gray-600">Fecha de inicio</p>
                        <Input
                          type="date"
                          value={modalStartDate}
                          onChange={handleModalStartDateChange}
                          max={modalEndDate}
                          min={earliestStartFor(modalEndDate)}
                          className="bg-white"
                        />
                      </div>
                      <div>
                        <p className="mb-1 text-xs text-gray-600">Fecha de fin</p>
                        <Input
                          type="date"
                          value={modalEndDate}
                          onChange={handleModalEndDateChange}
                          max={latestEndFor(modalStartDate, todayDate)}
                          min={modalStartDate}
                          className="bg-white"
                        />
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-600">
                      El rango máximo por reporte es de 2 meses.
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-600">Formato:</span>
                  <select
                    value={modalFormat}
                    onChange={(e) => setModalFormat(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:w-[200px]"
                  >
                    <option value="Excel (.xlsx)">Excel (.xlsx)</option>
                    <option value="CSV (.csv)">CSV (.csv)</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleGenerateReport}
              disabled={!modalSelectedReport || isCreatingJob}
            >
              <Download className="size-4" />
              Generar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainSectionSection>
  );
};

export default ReportsContainer;
